// src/services/api.js

/**
 * Enhanced API Service for AeroFi Node Application
 * Handles all the API calls to the AeroFi backend with improved error handling
 */

const API_BASE_URL = 'https://api.aeronyx.network/api';

/**
 * Helper function to ensure a signature is in the correct string format
 * @param {any} signature - The signature to format (could be Uint8Array, object, or string)
 * @returns {string} The formatted signature as a string
 */
const formatSignature = (signature) => {
  // If it's already a string, return it
  if (typeof signature === 'string') {
    return signature;
  }
  
  // If it's a Uint8Array, convert to Base64
  if (signature instanceof Uint8Array) {
    return bytesToBase64(signature);
  }
  
  // If it's an Array or array-like object, convert to Base64
  if (Array.isArray(signature) || (signature && typeof signature === 'object' && signature.length)) {
    return bytesToBase64(signature);
  }
  
  // If it has a toString method, use it (works for many objects including Buffer)
  if (signature && typeof signature.toString === 'function' && signature.toString !== Object.prototype.toString) {
    return signature.toString();
  }
  
  // If nothing else works, convert to JSON string
  if (signature && typeof signature === 'object') {
    try {
      return JSON.stringify(signature);
    } catch (e) {
      console.error('Failed to stringify signature:', e);
    }
  }
  
  // Last resort: empty string
  console.error('Could not format signature of type:', typeof signature);
  return '';
};

/**
 * Convert a byte array or Uint8Array to a Base64 string
 * @param {Uint8Array|Array} bytes - The byte array to convert
 * @returns {string} The Base64 encoded string
 */
const bytesToBase64 = (bytes) => {
  // If it's an array-like object but not a Uint8Array, convert it
  if (bytes && typeof bytes === 'object' && !bytes.buffer && Object.keys(bytes).length > 0) {
    bytes = Uint8Array.from(Object.values(bytes));
  }
  
  // Handle different types of input
  let binary = '';
  if (bytes instanceof Uint8Array) {
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
  } else if (Array.isArray(bytes)) {
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
  } else if (typeof bytes === 'string') {
    // Already a string, just return it
    return bytes;
  } else {
    console.error('Unknown signature format:', bytes);
    return '';
  }
  
  return btoa(binary);
};

/**
 * Handle API responses consistently with improved error handling
 * @param {Response} response - Fetch API response
 * @returns {Promise<Object>} Parsed response data
 */
const handleResponse = async (response) => {
  try {
    const contentType = response.headers.get('content-type');
    const isJson = contentType && contentType.includes('application/json');
    
    // Try to parse response as JSON
    const data = isJson ? await response.json() : null;
    
    if (!response.ok) {
      // Use detailed error info if available
      if (data && !data.success) {
        const errorMessage = data.message || `API error: ${response.status}`;
        const error = new Error(errorMessage);
        error.status = response.status;
        error.response = { data };
        throw error;
      }
      
      // Fallback error
      throw new Error(`API request failed with status: ${response.status}`);
    }
    
    // Return the full response object
    return data;
  } catch (error) {
    console.error('Error handling API response:', error);
    // Return object with success: false instead of throwing
    return { success: false, message: error.message || 'Unknown error' };
  }
};

/**
 * Generate a signature message for wallet authentication
 * @param {string} walletAddress The wallet address
 * @param {number} blockchainNetworkId The blockchain network ID
 * @param {string} walletType The wallet type identifier
 * @returns {Promise<Object>} The message details
 */
export const generateSignatureMessage = async (walletAddress, blockchainNetworkId, walletType) => {
  try {
    console.log('Generating signature message for:', walletAddress);
    
    // Ensure blockchain network ID is a number
    let networkId = blockchainNetworkId;
    if (typeof networkId === 'string') {
      if (networkId === 'ethereum') networkId = 1;
      else if (networkId === 'bsc') networkId = 2;
      else if (networkId === 'solana') networkId = 3;
      else if (!isNaN(parseInt(networkId))) networkId = parseInt(networkId);
      else networkId = 1; // Default to 1 if can't parse
    }
    
    if (isNaN(networkId)) networkId = 1; // Additional safety check
    
    console.log('Using network ID:', networkId);
    
    // Use the blockchain network ID as a number
    const response = await fetch(`${API_BASE_URL}/generate-signature-message/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        wallet_address: walletAddress,
        blockchain_network_id: networkId
      }),
    });
    
    const responseData = await handleResponse(response);
    console.log('Signature message response:', responseData);
    
    // Return the data object or a default value
    if (responseData && responseData.success && responseData.data) {
      return responseData.data;
    }
    
    return { message: '', timestamp: Date.now() };
  } catch (error) {
    console.error('Error generating signature message:', error);
    // Return a default object instead of throwing
    return { message: '', timestamp: Date.now() };
  }
};

/**
 * Sign a message using the wallet
 * @param {string} walletType - The wallet type
 * @param {string} address - Account address
 * @param {string} message - Message to sign
 * @returns {Promise<string>} Signature
 */
export const signMessage = async (walletType, address, message) => {
  try {
    // Normalize wallet type
    const type = typeof walletType === 'string' 
      ? walletType.toLowerCase() 
      : (walletType && walletType.type && typeof walletType.type === 'string' 
          ? walletType.type.toLowerCase() 
          : '');
    
    if (!type) {
      console.warn('Invalid wallet type for signing:', walletType);
      return ''; // Return empty string instead of throwing
    }
    
    // Get wallet provider
    let provider;
    if (type === 'phantom' && window.phantom?.solana) {
      provider = window.phantom.solana;
    } else if (type === 'solflare' && window.solflare) {
      provider = window.solflare;
    } else if (type === 'okx' && window.okxwallet) {
      // Check if we need Solana or EVM provider
      if (address && !address.startsWith('0x') && window.okxwallet.solana) {
        provider = window.okxwallet.solana;
      } else {
        provider = window.okxwallet;
      }
    } else if (window.ethereum) {
      // Default to ethereum provider for EVM wallets
      provider = window.ethereum;
    }
    
    if (!provider) {
      console.warn('No provider found for wallet type:', type);
      return ''; // Return empty string instead of throwing
    }
    
    // Handle Solana wallets
    if ((type === 'phantom' || type === 'solflare' || 
         (type === 'okx' && provider === window.okxwallet?.solana)) &&
        typeof provider.signMessage === 'function') {
      
      // Encode message for Solana
      const encodedMessage = new TextEncoder().encode(message);
      
      try {
        // Sign message
        const signResult = await provider.signMessage(encodedMessage, 'utf8');
        
        // Return signature in appropriate format
        if (signResult && signResult.signature) {
          return bytesToBase64(signResult.signature);
        } else if (signResult) {
          return bytesToBase64(signResult);
        }
        return '';
      } catch (solanaError) {
        console.error('Error signing Solana message:', solanaError);
        return '';
      }
    }
    
    // Handle EVM wallets
    if (provider && provider.request) {
      try {
        // Request personal signature (standard EVM method)
        const signature = await provider.request({
          method: 'personal_sign',
          params: [message, address]
        });
        
        return signature || '';
      } catch (evmError) {
        console.error('Error signing EVM message:', evmError);
        return '';
      }
    }
    
    console.warn('No signing method found for wallet type:', type);
    return ''; // Return empty string instead of throwing
  } catch (error) {
    console.error('Error in signMessage:', error);
    return ''; // Return empty string instead of throwing
  }
};

/**
 * Fetch available node tiers with enhanced error handling and response validation
 * @returns {Promise<Array>} The node tiers array
 */
export const fetchNodeTiers = async () => {
  try {
    // Use a try-catch block specifically for the fetch operation
    let response;
    try {
      response = await fetch(`${API_BASE_URL}/node-tiers/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (fetchError) {
      console.error('Network error while fetching node tiers:', fetchError);
      return []; // Return empty array instead of throwing
    }
    
    // First check for HTTP errors
    if (!response.ok) {
      console.error('Node tiers HTTP error:', response.status);
      return []; // Return empty array instead of throwing
    }
    
    // Try to parse as JSON with error handling
    let data;
    try {
      data = await response.json();
    } catch (jsonError) {
      console.error('Failed to parse JSON response:', jsonError);
      return []; // Return empty array instead of throwing
    }
    
    // Validate the data structure and return the appropriate format
    if (!data) {
      console.error('Empty response from API');
      return [];
    }
    
    // Handle different valid response formats
    if (Array.isArray(data)) {
      return data;
    } else if (data.data && Array.isArray(data.data)) {
      return data.data;
    } else if (data.success && data.data && Array.isArray(data.data)) {
      return data.data;
    } else {
      // If we can't find an array, but we have some data, try to log it
      console.error('Unexpected API response structure:', 
                    typeof data === 'object' ? JSON.stringify(data).substring(0, 500) : typeof data);
      return [];
    }
  } catch (error) {
    console.error('Error in fetchNodeTiers:', error);
    return []; // Return empty array instead of throwing
  }
};

/**
 * Submit a node application with improved error handling
 * @param {Object} applicationData The application data
 * @returns {Promise<Object>} The application result
 */
export const submitNodeApplication = async (applicationData) => {
  try {
    // Ensure signature is in the correct format
    if (applicationData.signature) {
      applicationData.signature = formatSignature(applicationData.signature);
    }
    
    const response = await fetch(`${API_BASE_URL}/node-applications/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(applicationData),
    });
    
    return handleResponse(response);
  } catch (error) {
    console.error('Error submitting application:', error);
    return { success: false, message: error.message || 'Failed to submit application' };
  }
};

/**
 * Fetch available blockchain networks
 * @returns {Promise<Array>} The blockchain networks
 */
export const fetchBlockchainNetworks = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/blockchain-networks/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    const data = await handleResponse(response);
    return Array.isArray(data) ? data : (data.data && Array.isArray(data.data) ? data.data : []);
  } catch (error) {
    console.error('Error fetching blockchain networks:', error);
    return [];
  }
};

/**
 * Fetch available wallet types
 * @returns {Promise<Array>} The wallet types
 */
export const fetchWalletTypes = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/wallet-types/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    const data = await handleResponse(response);
    return Array.isArray(data) ? data : (data.data && Array.isArray(data.data) ? data.data : []);
  } catch (error) {
    console.error('Error fetching wallet types:', error);
    return [];
  }
};

/**
 * Check the status of an application
 * @param {string} referenceCode The application reference code
 * @param {string} walletAddress The wallet address (optional)
 * @returns {Promise<Object>} The application status
 */
export const checkApplicationStatus = async (referenceCode, walletAddress) => {
  try {
    let url = `${API_BASE_URL}/node-applications/${referenceCode}/status/`;
    
    // Add wallet address as query parameter if provided
    if (walletAddress) {
      url += `?wallet_address=${walletAddress}`;
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    return handleResponse(response);
  } catch (error) {
    console.error('Error checking application status:', error);
    return { success: false, message: error.message || 'Failed to check application status' };
  }
};

/**
 * Get applications for a wallet address with improved error handling
 * @param {string} walletAddress The wallet address
 * @param {string} signature The signature (optional)
 * @param {string} message The message that was signed (optional)
 * @param {number} blockchainNetworkId The blockchain network ID (optional)
 * @param {string} walletType The wallet type identifier (optional)
 * @returns {Promise<Object>} The applications
 */
export const getWalletApplications = async (
  walletAddress, 
  signature = '', 
  message = '', 
  blockchainNetworkId = null, 
  walletType = ''
) => {
  try {
    // Format signature properly
    const formattedSignature = formatSignature(signature);
    
    // Prepare request body
    const requestBody = {
      wallet_address: walletAddress
    };
    
    // Add optional parameters if provided
    if (formattedSignature) requestBody.signature = formattedSignature;
    if (message) requestBody.message = message;
    if (blockchainNetworkId) requestBody.blockchain_network_id = blockchainNetworkId;
    if (walletType) requestBody.wallet_type = walletType;
    
    const response = await fetch(`${API_BASE_URL}/node-applications/wallet_applications/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    
    const result = await handleResponse(response);
    
    // Ensure applications is an array even if API fails
    if (result && result.success && result.applications && Array.isArray(result.applications)) {
      return { applications: result.applications };
    } else if (result && result.success && result.data && Array.isArray(result.data.applications)) {
      return { applications: result.data.applications };
    } else {
      return { applications: [] };
    }
  } catch (error) {
    console.error('Error fetching wallet applications:', error);
    return { applications: [] };
  }
};

/**
 * Submit payment for a node application
 * @param {Object} paymentData The payment data
 * @returns {Promise<Object>} The payment result
 */
export const submitPayment = async (paymentData) => {
  try {
    // Ensure signature is in the correct format if present
    if (paymentData.signature) {
      paymentData.signature = formatSignature(paymentData.signature);
    }
    
    const response = await fetch(`${API_BASE_URL}/payments/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(paymentData),
    });
    
    return handleResponse(response);
  } catch (error) {
    console.error('Error submitting payment:', error);
    return { success: false, message: error.message || 'Failed to submit payment' };
  }
};

/**
 * Withdraw referrer commissions using a dedicated endpoint
 * @param {string} walletAddress Wallet address
 * @param {string} amount Amount to withdraw
 * @param {string} signature Signature
 * @param {string} message Message that was signed
 * @param {string} walletType Wallet type (optional)
 * @returns {Promise<Object>} Withdrawal result
 */
export const withdrawReferrerCommissions = async (
  walletAddress,
  amount,
  signature,
  message,
  walletType = ''
) => {
  try {
    // Log withdrawal attempt with safe signature logging
    console.log('Attempting to withdraw referrer commissions:', {
      walletAddress,
      amount,
      signature: typeof signature === 'string' ? 
                (signature.substring(0, 10) + '...') : 
                'Non-string signature',
      message: message ? message.substring(0, 20) + '...' : null,
      walletType
    });
    
    // Ensure signature is properly formatted as a string
    const formattedSignature = formatSignature(signature);
    
    // Prepare request body
    const requestBody = {
      wallet_address: walletAddress,
      amount: amount,
      signature: formattedSignature,
      message: message
    };
    
    // Add optional parameters if provided
    if (walletType) requestBody.wallet_type = walletType;
    
    // Call the dedicated referrer commissions withdrawal API endpoint
    const response = await fetch(`${API_BASE_URL}/commissions/withdraw-referrer-commissions/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    
    const responseData = await handleResponse(response);
    console.log('Withdraw referrer commissions response:', responseData);
    
    return responseData;
  } catch (error) {
    console.error('Error withdrawing referrer commissions:', error);
    return { 
      success: false, 
      message: error.message || 'Failed to withdraw referrer commissions'
    };
  }
};

/**
 * Get user's withdrawal history with type filtering
 * @param {string} walletAddress Wallet address
 * @param {string} signature Signature
 * @param {string} message Message that was signed
 * @param {string} walletType Wallet type (optional)
 * @param {number} page Page number (optional, default: 1)
 * @param {number} pageSize Number of records per page (optional, default: 10)
 * @param {string} status Filter by status (optional, e.g., 'pending', 'completed', 'rejected')
 * @param {string} withdrawalType Filter by type (optional, e.g., 'node', 'referrer')
 * @returns {Promise<Object>} Withdrawal history
 */
export const getUserWithdrawals = async (
  walletAddress,
  signature,
  message,
  walletType = '',
  page = 1,
  pageSize = 10,
  status = '',
  withdrawalType = ''
) => {
  try {
    // Ensure signature is properly formatted as a string
    const formattedSignature = formatSignature(signature);
    
    // Prepare request body
    const requestBody = {
      wallet_address: walletAddress,
      signature: formattedSignature,
      message: message,
      page: page,
      page_size: pageSize
    };
    
    // Add optional parameters if provided
    if (walletType) requestBody.wallet_type = walletType;
    if (status) requestBody.status = status;
    if (withdrawalType) requestBody.withdrawal_type = withdrawalType;
    
    const response = await fetch(`${API_BASE_URL}/commissions/withdrawal-history/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    
    const responseData = await handleResponse(response);
    
    // Return the success response or provide default structure
    if (responseData && responseData.success) {
      return responseData;
    } else {
      return {
        success: false,
        message: responseData?.message || 'Failed to fetch withdrawal history',
        data: {
          withdrawals: [],
          pagination: {
            total_items: 0,
            total_pages: 1,
            current_page: page,
            page_size: pageSize,
            has_next: false,
            has_previous: false
          }
        }
      };
    }
  } catch (error) {
    console.error('Error fetching withdrawal history:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch withdrawal history',
      data: {
        withdrawals: [],
        pagination: {
          total_items: 0,
          total_pages: 1,
          current_page: page,
          page_size: pageSize,
          has_next: false,
          has_previous: false
        }
      }
    };
  }
};

/**
 * Get withdrawal details
 * @param {string} withdrawalId Withdrawal ID
 * @param {string} walletAddress Wallet address
 * @param {string} signature Signature
 * @param {string} message Message that was signed
 * @param {string} walletType Wallet type (optional)
 * @returns {Promise<Object>} Withdrawal details
 */
export const getWithdrawalDetails = async (
  withdrawalId,
  walletAddress,
  signature,
  message,
  walletType = ''
) => {
  try {
    // Ensure signature is properly formatted as a string
    const formattedSignature = formatSignature(signature);
    
    // Prepare request body
    const requestBody = {
      withdrawal_id: withdrawalId,
      wallet_address: walletAddress,
      signature: formattedSignature,
      message: message
    };
    
    // Add optional parameters if provided
    if (walletType) requestBody.wallet_type = walletType;
    
    const response = await fetch(`${API_BASE_URL}/commissions/withdrawal-details/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    
    const responseData = await handleResponse(response);
    
    if (responseData && responseData.success) {
      return responseData;
    } else {
      return {
        success: false,
        message: responseData?.message || 'Failed to get withdrawal details',
        data: null
      };
    }
  } catch (error) {
    console.error('Error getting withdrawal details:', error);
    return { 
      success: false, 
      message: error.message || 'Failed to get withdrawal details',
      data: null
    };
  }
};




/**
 * Generate or retrieve invitation code with improved error handling
 * @param {string} walletAddress Wallet address
 * @param {number} blockchainNetworkId Blockchain network ID
 * @param {string} signature Signature (optional)
 * @param {string} message Message (optional)
 * @param {string} walletType Wallet type (optional)
 * @returns {Promise<Object>} Invitation code data
 */
export const generateInvitationCode = async (
  walletAddress, 
  blockchainNetworkId, 
  signature = '', 
  message = '', 
  walletType = ''
) => {
  try {
    // Safe logging of signature
    console.log('Generating invitation code with params:', {
      walletAddress,
      blockchainNetworkId, 
      signature: typeof signature === 'string' ? 
                 (signature.substring(0, 10) + '...') : 
                 'Non-string signature',
      message: message ? message.substring(0, 20) + '...' : null,
      walletType
    });
    
    // Ensure signature is properly formatted as a string
    const formattedSignature = formatSignature(signature);
    
    // Prepare request body
    const requestBody = {
      wallet_address: walletAddress,
      blockchain_network_id: blockchainNetworkId
    };
    
    // Add optional parameters if provided
    if (formattedSignature) requestBody.signature = formattedSignature;
    if (message) requestBody.message = message;
    if (walletType) requestBody.wallet_type = walletType;
    
    const response = await fetch(`${API_BASE_URL}/invitation-code/generate/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    
    const responseData = await handleResponse(response);
    console.log('Generate invitation code response:', responseData);
    
    return responseData;
  } catch (error) {
    console.error('Error generating invitation code:', error);
    return { success: false, message: error.message || 'Failed to generate invitation code' };
  }
};

/**
 * Apply invitation code with improved error handling
 * @param {string} invitationCode Invitation code
 * @param {string} walletAddress Wallet address
 * @param {number} blockchainNetworkId Blockchain network ID
 * @param {string} signature Signature (optional)
 * @param {string} message Message (optional)
 * @param {string} walletType Wallet type (optional)
 * @returns {Promise<Object>} Result
 */
export const applyInvitationCode = async (
  invitationCode, 
  walletAddress, 
  blockchainNetworkId, 
  signature = '', 
  message = '', 
  walletType = ''
) => {
  try {
    // Ensure signature is properly formatted as a string
    const formattedSignature = formatSignature(signature);
    
    // Prepare request body
    const requestBody = {
      invitation_code: invitationCode,
      wallet_address: walletAddress,
      blockchain_network_id: blockchainNetworkId
    };
    
    // Add optional parameters if provided
    if (formattedSignature) requestBody.signature = formattedSignature;
    if (message) requestBody.message = message;
    if (walletType) requestBody.wallet_type = walletType;
    
    const response = await fetch(`${API_BASE_URL}/invitation-code/apply/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    
    return handleResponse(response);
  } catch (error) {
    console.error('Error applying invitation code:', error);
    return { success: false, message: error.message || 'Failed to apply invitation code' };
  }
};

/**
 * Check the status of a referral code with improved error handling
 * @param {string} walletAddress The wallet address to check
 * @returns {Promise<Object>} The referral status
 */
export const checkReferralStatus = async (walletAddress) => {
  try {
    const response = await fetch(`${API_BASE_URL}/invitation-code/check-status/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        wallet_address: walletAddress
      }),
    });
    
    return handleResponse(response);
  } catch (error) {
    console.error('Error checking referral status:', error);
    return { success: false, message: error.message || 'Failed to check referral status' };
  }
};

/**
 * Get referrals with improved error handling
 * @param {string} walletAddress Wallet address
 * @param {number} blockchainNetworkId Blockchain network ID
 * @param {string} signature Signature (optional)
 * @param {string} message Message (optional)
 * @param {string} walletType Wallet type (optional)
 * @returns {Promise<Object>} Referrals data
 */
export const getReferrals = async (
  walletAddress, 
  blockchainNetworkId, 
  signature = '', 
  message = '', 
  walletType = ''
) => {
  try {
    // Safe logging of signature
    console.log('Fetching referrals with params:', {
      walletAddress,
      blockchainNetworkId,
      signature: typeof signature === 'string' ? 
                 (signature.substring(0, 10) + '...') : 
                 'Non-string signature', 
      message: message ? message.substring(0, 20) + '...' : null,
      walletType
    });
    
    // Ensure signature is properly formatted as a string
    const formattedSignature = formatSignature(signature);
    
    // Prepare request body
    const requestBody = {
      wallet_address: walletAddress,
      blockchain_network_id: blockchainNetworkId
    };
    
    // Add optional parameters if provided
    if (formattedSignature) requestBody.signature = formattedSignature;
    if (message) requestBody.message = message;
    if (walletType) requestBody.wallet_type = walletType;
    
    const response = await fetch(`${API_BASE_URL}/invitation-code/referrals/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    
    const responseData = await handleResponse(response);
    console.log('Referrals API full response:', responseData);
    
    // Return the full response object or default values
    return responseData && responseData.success ? responseData : { 
      success: false, 
      message: 'Failed to fetch referrals',
      data: { referrals: [] }
    };
  } catch (error) {
    console.error('Error fetching referrals:', error);
    return { 
      success: false, 
      message: error.message || 'Failed to fetch referrals',
      data: { referrals: [] }
    };
  }
};

/**
 * Get referrer commissions data with improved error handling and signature caching
 * @param {string} walletAddress Wallet address
 * @param {string} signature Signature
 * @param {string} message Message that was signed
 * @param {string} walletType Wallet type (optional)
 * @param {string} period Time period for commissions ('7d', '30d', '90d', 'all') (default: '30d')
 * @param {number} page Page number for referrals list (default: 1)
 * @param {number} pageSize Number of records per page (default: 20, max: 100)
 * @returns {Promise<Object>} Referrer commissions data
 */
export const getReferrerCommissionsData = async (
  walletAddress,
  signature,
  message,
  walletType = '',
  period = '30d',
  page = 1,
  pageSize = 20
) => {
  try {
    // Safe logging of signature
    console.log('Fetching referrer commissions data with params:', {
      walletAddress,
      signature: typeof signature === 'string' ? 
                (signature.substring(0, 10) + '...') : 
                'Non-string signature',
      message: message ? message.substring(0, 20) + '...' : null,
      walletType,
      period,
      page,
      pageSize
    });
    
    // Ensure signature is properly formatted as a string
    const formattedSignature = formatSignature(signature);
    
    // Prepare request body
    const requestBody = {
      wallet_address: walletAddress,
      signature: formattedSignature,
      message: message,
      period: period,
      page: page,
      page_size: pageSize
    };
    
    // Add optional parameters if provided
    if (walletType) requestBody.wallet_type = walletType;
    
    const response = await fetch(`${API_BASE_URL}/commissions/referrer-data/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    
    const responseData = await handleResponse(response);
    console.log('Referrer commissions data API response:', responseData);
    
    // Return the success response or provide default structure
    if (responseData && responseData.success) {
      return responseData;
    } else {
      return {
        success: false,
        message: responseData?.message || 'Failed to fetch referrer commissions data',
        data: {
          summary: {
            total_referrer_commissions: 0,
            total_referrals_count: 0,
            active_referrals_count: 0,
            commissions_count: 0,
            period: period
          },
          commissions: [],
          referrals: {
            records: [],
            pagination: {
              total_records: 0,
              total_pages: 1,
              current_page: page,
              page_size: pageSize
            }
          }
        }
      };
    }
  } catch (error) {
    console.error('Error fetching referrer commissions data:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch referrer commissions data',
      data: {
        summary: {
          total_referrer_commissions: 0,
          total_referrals_count: 0,
          active_referrals_count: 0,
          commissions_count: 0,
          period: period
        },
        commissions: [],
        referrals: {
          records: [],
          pagination: {
            total_records: 0,
            total_pages: 1,
            current_page: page,
            page_size: pageSize
          }
        }
      }
    };
  }
};

/**
 * Withdraw commissions with improved error handling
 * @param {string} walletAddress Wallet address
 * @param {string} amount Amount to withdraw
 * @param {string} signature Signature
 * @param {string} message Message that was signed
 * @param {string} walletType Wallet type (optional)
 * @returns {Promise<Object>} Withdrawal result
 */
export const withdrawCommissions = async (
  walletAddress,
  amount,
  signature,
  message,
  walletType = ''
) => {
  try {
    // Log withdrawal attempt with safe signature logging
    console.log('Attempting to withdraw commissions:', {
      walletAddress,
      amount,
      signature: typeof signature === 'string' ? 
                (signature.substring(0, 10) + '...') : 
                'Non-string signature',
      message: message ? message.substring(0, 20) + '...' : null,
      walletType
    });
    
    // Ensure signature is properly formatted as a string
    const formattedSignature = formatSignature(signature);
    
    // Prepare request body
    const requestBody = {
      wallet_address: walletAddress,
      amount: amount,
      signature: formattedSignature,
      message: message
    };
    
    // Add optional parameters if provided
    if (walletType) requestBody.wallet_type = walletType;
    
    const response = await fetch(`${API_BASE_URL}/commissions/withdraw/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    
    const responseData = await handleResponse(response);
    console.log('Withdraw commissions response:', responseData);
    
    return responseData;
  } catch (error) {
    console.error('Error withdrawing commissions:', error);
    return { 
      success: false, 
      message: error.message || 'Failed to withdraw commissions'
    };
  }
};

export default {
  generateSignatureMessage,
  signMessage,
  submitNodeApplication,
  fetchBlockchainNetworks,
  fetchNodeTiers,
  fetchWalletTypes,
  checkApplicationStatus,
  getWalletApplications,
  submitPayment,
  generateInvitationCode,
  applyInvitationCode,
  checkReferralStatus,
  getReferrals,
  getReferrerCommissionsData,
  withdrawReferrerCommissions,
  getUserWithdrawals,
  getWithdrawalDetails
};
